<script setup lang="ts">
import type { LawReference } from '~/utils/solr/academic-article'
import { LAW_BASE_PATH } from '#shared/constants'
import { SolrParamsKey } from '#shared/solr'
import { faArrowRight, faChevronDown, faChevronUp, faTimes } from '@fortawesome/pro-light-svg-icons'
import { useSolrQueryV2 } from '~/composables/useSolrV2'

const props = defineProps<{
  id: string
}>()

const emit = defineEmits<{
  remove: []
}>()
const { data, isLoading } = useSolrQueryV2(
  computed(() => ({ [SolrParamsKey.Ids]: [props.id] })),
  'lawReference',
  'literature',
)

const reference = computed<LawReference | undefined>(() => data.value?.response?.docs?.[0])

const expanded = ref(true)

const combinedBodyText = computed(() => {
  const children = reference?.value?.children ?? []

  // Leaf node (subsection) — bodytext lives directly on the reference
  if (children.length === 0 && reference?.value?.bodytext) {
    return `<p>${reference?.value?.bodytext.trim()}</p>`
  }

  // Normal section — bodytext lives on children
  return children
    .map((child) => {
      const bodytext = child.bodytext?.trim()
      if (!bodytext) {
        return ''
      }
      const childId = typeof child.id === 'string' ? child.id : String(child.id ?? '')
      const isNote = /note-n?\d+/i.test(childId)
      const classAttr = isNote ? ' class="text-caption [&_*]:text-caption"' : ''
      return `<p${classAttr}>${bodytext}</p>`
    })
    .filter(Boolean)
    .join('')
})

const lawLink = computed(() => {
  const id = (reference?.value?.id || '').toString().toLowerCase()
  if (!id) {
    return undefined
  }
  return `/${LAW_BASE_PATH}/${id}`
})
</script>

<template>
  <div class="px-s pb-s flex flex-col gap-s animate-slide-up">
    <div class="flex items-center gap-x-m py-xs">
      <div class="flex flex-col gap-3xs flex-grow">
        <span class="text-caption text-secondary">
          Henvisning&nbsp;til&nbsp;lov
        </span>
        <span v-if="reference?.title && !isLoading" :class="reference?.title || reference?.paragraphTitle ? 'text-small' : 'text-caption text-carbon-500 dark:text-carbon-100 italic'">
          {{ reference?.title ? reference.title : reference?.paragraphTitle ? reference.paragraphTitle : 'Lovreferanse finnes ikke' }}
        </span>
        <div v-else-if="isLoading" class="flex flex-col">
          <Skeleton class="h-5 w-3/4 bg-carbon-200 dark:bg-surface-hover rounded-2xl mb-2" />
          <Skeleton class="h-5 w-1/4 bg-carbon-200 dark:bg-surface-hover rounded-2xl" />
        </div>
        <span v-else-if="!isLoading && !reference" class="text-caption text-carbon-500 dark:text-carbon-100 italic">
          Fant ikke lovreferanse
        </span>
      </div>
      <div class="flex items-center gap-2xs">
        <ControlButton
          v-if="reference"
          :aria-label="expanded ? 'Skjul lovreferanse' : 'Utvid lovreferanse'"
          :selected="expanded"
          type="outlined"
          :icon="expanded ? faChevronUp : faChevronDown"
          @click="expanded = !expanded"
        />
        <ControlButton aria-label="Fjern lovreferanse" type="outlined" :icon="faTimes" @click="emit('remove')" />
      </div>
    </div>
    <div v-if="expanded && reference" class="flex flex-col gap-s">
      <BodyText
        v-if="combinedBodyText && !isLoading"
        class="max-h-52 overflow-y-auto"
        source="ibexa"
        :html="combinedBodyText"
      />
      <ControlButton v-if="lawLink" aria-label="Vis hele loven (åpnes i ny fane)" type="outlined" :icon="faArrowRight" :href="lawLink" size="large" target="_blank" class="gap-xs">
        Vis&nbsp;hele&nbsp;loven
      </ControlButton>
    </div>
  </div>
</template>

<style scoped lang="postcss">
:deep(.body-text) {
  @apply text-sm;

  * {
    @apply !text-sm !mb-2xs;
  }

  .text-caption,
  .text-caption * {
    @apply !text-xs mt-2xs;
  }
}
</style>
